package com.evoapps.evoshift.ui.legal

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.DataExportViewModel
import com.evoapps.evoshift.viewmodels.ExportTarget
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.export_download
import evoshift.composeapp.generated.resources.export_my_data
import evoshift.composeapp.generated.resources.export_my_data_text
import evoshift.composeapp.generated.resources.export_ready_text
import evoshift.composeapp.generated.resources.export_ready_title
import evoshift.composeapp.generated.resources.export_sign_in_again
import evoshift.composeapp.generated.resources.export_sign_in_again_text
import evoshift.composeapp.generated.resources.export_sign_out
import evoshift.composeapp.generated.resources.export_workplace
import evoshift.composeapp.generated.resources.export_workplace_text
import evoshift.composeapp.generated.resources.export_working
import evoshift.composeapp.generated.resources.legal_dpa_note
import evoshift.composeapp.generated.resources.legal_privacy
import evoshift.composeapp.generated.resources.legal_privacy_url
import evoshift.composeapp.generated.resources.legal_signup_note
import evoshift.composeapp.generated.resources.legal_terms
import evoshift.composeapp.generated.resources.legal_terms_url
import org.jetbrains.compose.resources.stringResource

// Privacy entries shared by several screens (SH-27, SH-50): links to the privacy policy and the
// terms (Apple 3.1.2: also on the paywall), the acceptance notes, and the data export entry.
// Each is one self-contained composable, wired into a screen with one line.

/** "Privacy policy · Terms of use", opening evoshift.app in the app's language. */
@Composable
fun LegalLinks(modifier: Modifier = Modifier, color: Color = MaterialTheme.colorScheme.primary) {
    val uri = LocalUriHandler.current
    val privacyUrl = stringResource(Res.string.legal_privacy_url)
    val termsUrl = stringResource(Res.string.legal_terms_url)
    FlowRow(
        modifier = modifier.fillMaxWidth().testTag("legal_links"),
        horizontalArrangement = Arrangement.Center,
        itemVerticalAlignment = Alignment.CenterVertically
    ) {
        TextButton(onClick = { uri.openUri(privacyUrl) }) {
            Text(stringResource(Res.string.legal_privacy), color = color, style = MaterialTheme.typography.labelMedium)
        }
        Text("·", color = color)
        TextButton(onClick = { uri.openUri(termsUrl) }) {
            Text(stringResource(Res.string.legal_terms), color = color, style = MaterialTheme.typography.labelMedium)
        }
    }
}

/** Under the sign-up / sign-in buttons: signing up accepts the terms (recorded with the version). */
@Composable
fun SignUpLegalNote(modifier: Modifier = Modifier, color: Color = MaterialTheme.colorScheme.onSurfaceVariant) {
    LegalNote(stringResource(Res.string.legal_signup_note), modifier, color)
}

/** When an owner opens a workplace: the data processing annex of the terms applies (Art. 28). */
@Composable
fun WorkplaceDpaNote(modifier: Modifier = Modifier, color: Color = MaterialTheme.colorScheme.onSurfaceVariant) {
    LegalNote(stringResource(Res.string.legal_dpa_note), modifier, color)
}

@Composable
private fun LegalNote(text: String, modifier: Modifier, color: Color) {
    Column(modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
        Text(text, color = color, style = MaterialTheme.typography.bodySmall, textAlign = TextAlign.Center)
        LegalLinks(color = color)
    }
}

/**
 * "Download my data" (no [workplaceId]) or "Export workplace data": the server builds a ZIP and
 * the dialog offers the 24-hour link, opened on the person's tap (a link opened after a wait
 * would be blocked as a pop-up on the web).
 */
@Composable
fun DataExportEntry(workplaceId: String? = null, modifier: Modifier = Modifier) {
    val viewModel: DataExportViewModel = diViewModel(ExportTarget(workplaceId))
    val state by viewModel.state.collectAsState()
    val uri = LocalUriHandler.current
    val mine = workplaceId == null

    OutlinedButton(
        onClick = { viewModel.export() },
        enabled = !state.isWorking,
        modifier = modifier.fillMaxWidth().testTag(if (mine) "export_my_data" else "export_workplace")
    ) {
        Icon(Icons.Default.Download, contentDescription = null)
        Text(
            stringResource(if (state.isWorking) Res.string.export_working else if (mine) Res.string.export_my_data else Res.string.export_workplace),
            modifier = Modifier.padding(start = 8.dp)
        )
    }

    val ready = state.ready
    when {
        ready != null -> EvoDialog(
            title = stringResource(Res.string.export_ready_title),
            icon = Icons.Default.Download,
            onDismiss = viewModel::dismiss,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.export_download), { uri.openUri(ready.url) }),
            dismiss = DialogAction(stringResource(Res.string.close), viewModel::dismiss)
        ) {
            Text(stringResource(Res.string.export_ready_text, ready.fileName))
            Text(stringResource(if (mine) Res.string.export_my_data_text else Res.string.export_workplace_text), style = MaterialTheme.typography.bodySmall)
        }
        state.needsSignIn -> EvoDialog(
            title = stringResource(Res.string.export_sign_in_again),
            icon = Icons.Default.Lock,
            onDismiss = viewModel::dismiss,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.export_sign_out), viewModel::signOutToSignInAgain)
        ) {
            Text(stringResource(Res.string.export_sign_in_again_text))
        }
    }
    state.error?.let { error ->
        Text(error.asString(), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall, textAlign = TextAlign.Center, modifier = Modifier.fillMaxWidth())
    }
}

